import { Fragment, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useHistory } from 'react-router-dom';
import { searchJobCategories } from '../_api';
import {
	AUTOCOMPLETE_SEARCH_TYPE,
	BEM_NAME_ELEMENTS,
	DEFAULT_LIST_ITEM,
} from '../_const';
import AsyncAutocomplete from '../components/AsyncAutocomplete';
import CircularProgressWithLabel from '../components/CircularProgressWithLabel';
import PageWithBreadcrumbs from '../layouts/PageWithBreadcrumbs';
import JobCategoryDetails from './DetailsView';

/**
 * Epic Job Category Search page
 * @param {any} props properties from router
 * @returns Job Category Search page view
 */
const SearchView = props => {
	const history = useHistory();
	const query = new URLSearchParams(props.location.search);
	const id = query.get('id');
	const [sIsResultsExpanded, setIsResultsExpanded] = useState(false);
	const [sSearchValue, setSearchValue] = useState('');
	const [sIsSearching, setIsSearching] = useState(false);
	const [sSelectionId, setSelectionId] = useState(id ?? null);
	const [sResults, setResults] = useState([]);
	const [sIsArchiveSearch, setIsArchiveSearch] = useState(false);

	/**
	 * search API for job categories
	 */
	const search = async () => {
		setIsSearching(true);
		const results = await searchJobCategories(
			sSearchValue,
			true,
			sIsArchiveSearch
		);
		setResults(results.length > 0 ? results : [{ ...DEFAULT_LIST_ITEM }]);
		setIsResultsExpanded(true);
		setIsSearching(false);
	};

	/**
	 * handle user interaction with clicking on search results
	 * @param {number} id primary key for job category
	 */
	const onClick = async id => {
		setIsResultsExpanded(false);
		setSelectionId(id);
	};

	return (
		<PageWithBreadcrumbs currentPageLabel='Job Category Search'>
			<Fragment key={props.key}>
				<Typography
					variant='h6'
					color='primary'
					align='center'
					gutterBottom
				>
					Epic Job Category Search
				</Typography>
				<Typography variant='body1' align='center' gutterBottom>
					To index Epic job categories and their respective
					attributes, search below by name.
				</Typography>
				<Box
					sx={{
						display: 'flex',
						maxWidth: {
							sm: '100%',
							md: '100%',
						},
						justifyContent: 'flex-start',
						flexDirection: {
							sm: 'column',
							md: 'row',
						},
					}}
				>
					<AsyncAutocomplete
						id={`${BEM_NAME_ELEMENTS.VIEW_SEARCH}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--job-category-name`}
						type={AUTOCOMPLETE_SEARCH_TYPE.JOB_CATEGORY}
						archival={sIsArchiveSearch}
						label='Job Category Name'
						placeholder='Search for Job Category'
						handleChange={value => {
							setSearchValue(value?.label ?? value);
							setSelectionId(value?.id ?? null);
							setResults([]);
						}}
						handleKeystroke={value => {
							setSearchValue(value);
						}}
						value={sSearchValue}
						sx={{ flexGrow: 1 }}
						freeSolo
					/>
					<Button
						variant={'contained'}
						disabled={!sSearchValue}
						onClick={search}
						id={`${BEM_NAME_ELEMENTS.VIEW_SEARCH}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--job-category-search`}
					>
						Search
					</Button>
				</Box>
				<FormControlLabel
					control={
						<Checkbox
							checked={sIsArchiveSearch}
							onChange={() =>
								setIsArchiveSearch(!sIsArchiveSearch)
							}
							id={`${BEM_NAME_ELEMENTS.VIEW_SEARCH}__${BEM_NAME_ELEMENTS.FIELD_CHECKBOX}--job-category-archive`}
						/>
					}
					label='Archive Search: Include Inactive Job Categories'
				/>

				{sIsSearching ? (
					<Stack direction='column' alignItems='center' gap={1}>
						<CircularProgressWithLabel label='Searching...' />
					</Stack>
				) : (
					sResults.length > 0 && (
						<Accordion
							id={`${BEM_NAME_ELEMENTS.VIEW_RESULTS}__${BEM_NAME_ELEMENTS.FIELD_ACCORDION}--base`}
							sx={{
								borderRadius: '8px',
								marginTop: '32px',
								'&:before': {
									backgroundColor: 'unset',
								},
							}}
							disableGutters
							elevation={2}
							expanded={sIsResultsExpanded}
							onChange={() =>
								setIsResultsExpanded(state => !state)
							}
							square={false}
						>
							<AccordionSummary
								id={`${BEM_NAME_ELEMENTS.VIEW_RESULTS}__${BEM_NAME_ELEMENTS.FIELD_ACCORDION}--summary`}
								sx={{
									fontFamily: 'default',
									fontWeight: 'bold',
								}}
								expandIcon={
									<ExpandMoreIcon
										id={`${BEM_NAME_ELEMENTS.VIEW_RESULTS}__${BEM_NAME_ELEMENTS.FIELD_ACCORDION}--icon`}
									/>
								}
							>
								Search Results
							</AccordionSummary>

							<AccordionDetails
								sx={{
									padding: '0px 8px 8px 8px',
								}}
							>
								<List
									id={`${BEM_NAME_ELEMENTS.VIEW_RESULTS}__${BEM_NAME_ELEMENTS.FIELD_LIST}--base`}
									sx={{
										maxHeight: '200px',
										overflowY: 'auto',
										paddingTop: '0px',
										paddingBottom: '0px',
									}}
									dense
								>
									{sResults.map((jc, index) => (
										<ListItemButton
											id={`${BEM_NAME_ELEMENTS.VIEW_SEARCH}__${BEM_NAME_ELEMENTS.FIELD_LIST}--${jc.id}`}
											divider={
												index !== sResults.length - 1
											}
											key={jc.id}
											disabled={
												jc.id === DEFAULT_LIST_ITEM.id
											}
											onClick={() => onClick(jc.id)}
											selected={jc.id === sSelectionId}
										>
											<ListItemText primary={jc.label} />
										</ListItemButton>
									))}
								</List>
							</AccordionDetails>
						</Accordion>
					)
				)}

				{sSelectionId && (
					<Box sx={{ pb: 12, mt: 2 }}>
						<Divider />
						<Button
							variant={'contained'}
							color={'primary'}
							onClick={() =>
								history.push(
									'/EpicUtilities/JobCategory/Edit?id=' +
										sSelectionId
								)
							}
							startIcon={<EditIcon />}
							sx={{ mr: 3, mt: 2 }}
						>
							Edit
						</Button>
						<JobCategoryDetails
							key={sSelectionId}
							id={sSelectionId}
						/>
					</Box>
				)}
			</Fragment>
		</PageWithBreadcrumbs>
	);
};

export default SearchView;
